import { useEffect, useMemo, useState } from 'react';
import {
  documents,
  documentsByCategory,
  featuredDocuments,
} from '../data/documents';
import { searchPortalDocuments } from '../data/pageDocuments';
import { RouteLink, useLocationInfo, navigate } from '../route';
import PortalLayout from './PortalLayout';

export default function HomePage() {
  const { search } = useLocationInfo();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(search);
    setQuery(params.get('q') || '');
  }, [search]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    // Update URL as user types
    if (value.trim()) {
      navigate(`/?q=${encodeURIComponent(value)}`);
    } else {
      navigate('/');
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const value = query.trim();
    if (value) {
      navigate(`/?q=${encodeURIComponent(value)}`);
    } else {
      navigate('/');
    }
  };

  const searchMatches = useMemo(() => {
    const normalized = query.trim();
    return normalized ? searchPortalDocuments(normalized) : [];
  }, [query]);

  const groupedResults = useMemo(
    () =>
      documentsByCategory
        .map((group) => ({
          ...group,
          items: query.trim()
            ? searchMatches.filter((document) => document.category === group.category)
            : group.items,
        }))
        .filter((group) => group.items.length > 0),
    [query, searchMatches]
  );

  return (
    <PortalLayout>
      <section className="home-hero">
        <div className="home-hero-copy">
          <p className="hero-kicker">EduX Knowledge Portal</p>
          <h1>Professional module docs with faster discovery and cleaner reading.</h1>
          <p>
            Explore every EduX module through a lighter, more editorial portal
            designed for real browsing, quick handoffs, and route-based reference.
          </p>

          <div className="hero-actions">
            <RouteLink className="action-primary" to="/admission">
              Start with Admission
            </RouteLink>
            {/* <RouteLink className="action-secondary" to="/payroll">
              Open Payroll Docs
            </RouteLink> */}
          </div>
        </div>

        <div className="home-hero-panel">
          <div className="search-panel">
            <label htmlFor="doc-search">Search the documentation hub</label>
            <form onSubmit={handleSearchSubmit}>
              <input
                id="doc-search"
                type="search"
                placeholder="Search by module, workflow, or category"
                value={query}
                onChange={handleSearchChange}
              />
            </form>
          </div>

          <div className="summary-grid">
            <article>
              <span>Modules</span>
              <strong>{documents.length}</strong>
            </article>
            <article>
              <span>Categories</span>
              <strong>{documentsByCategory.length}</strong>
            </article>
            <article>
              <span>Results</span>
              <strong>{query.trim() ? searchMatches.length : documents.length}</strong>
            </article>
          </div>
        </div>
      </section>

      <section className="featured-card">
        <div className="section-heading-row">
          <div>
            <p className="section-eyebrow">Featured Modules</p>
            <h2>Start from the routes your teams open most often</h2>
          </div>
        </div>

        <div className="featured-grid">
          {featuredDocuments.map((document) => (
            <RouteLink className="featured-item" key={document.slug} to={document.route}>
              <span className="featured-category">{document.category}</span>
              <strong>{document.title}</strong><br/>
              <p>{document.description}</p>
              <span className="featured-cta">Open module page</span>
            </RouteLink>
          ))}
        </div>
      </section>

      <section className="catalog-card">
        <div className="section-heading-row">
          <div>
            <p className="section-eyebrow">
              {query.trim() ? 'Search Results' : 'Module Directory'}
            </p>
            <h2>
              {query.trim()
                ? `Results for "${query.trim()}"`
                : 'Browse the full documentation library by category'}
            </h2>
          </div>
        </div>

        {groupedResults.length ? (
          <div className="catalog-sections">
            {groupedResults.map((group) => (
              <section className="catalog-section" key={group.category}>
                <div className="catalog-section-header">
                  <h3>{group.category}</h3>
                  <span>{group.items.length} modules</span>
                </div>

                <div className="catalog-grid">
                  {group.items.map((document) => (
                    <RouteLink
                      className="catalog-item"
                      key={document.slug}
                      to={document.matchRoute || document.route}
                    >
                      <div className="catalog-meta">
                        <span>
                          {query.trim() ? `Match in ${document.matchLabel}` : document.category}
                        </span>
                      </div>
                      <strong>{document.title}</strong>
                      <p>{query.trim() ? document.matchSnippet : document.description}</p>
                    </RouteLink>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No modules matched your search yet.</p>
            <span>Try a broader term like payroll, records, or academics.</span>
          </div>
        )}
      </section>
    </PortalLayout>
  );
}
